import React, { useState, useEffect } from "react";
import { ErrorMessage, Formik } from "formik";
import {
  Alert,
  Form,
  Card,
  Row,
  Col,
  Accordion,
  Button,
  InputGroup, Spinner,
} from "react-bootstrap";
import * as Yup from "yup";
import { getInputClasses } from "@utils/get-input-classes";
import AlertError from "@components/shared/alert-error";
import { update } from "@services/hospitalization";
import { useFormRegistration } from "@hooks/use-form-registration";
import NumberFormat from "react-number-format";
import {ListReviewCuts} from "@components/panels/insured-file/review-cuts/list-review-cuts";
import {getAllReviewCuts, saveReviewCuts} from "@services/review-cuts";
import {useAuthStore} from "@stores/auth";
import {HospitalSupervisionBinnacleModal} from "@components/modals/hospital-supervision-binnacle-modal";

export const HospitalSupervision = (props) => {
  const [hasErrors, setHasErrors] = useState(false);

  return (
    <>
      <Card>
        <Card.Header style={hasErrors ? { backgroundColor: "#f8d7da", borderColor: "#f5c6cb" } : {}}>
          <Accordion.Toggle
            as={Button}
            variant="link"
            eventKey={props.eventKey}
          >
            {hasErrors && <i className="fa fa-exclamation-triangle text-danger mr-2"></i>}
            Supervisión Hospitalaria
            {hasErrors && <span className="text-danger ml-2" style={{ fontSize: "12px" }}>(Campos requeridos pendientes)</span>}
          </Accordion.Toggle>
          <i className={`fa ${props.icon}`} style={{position:"absolute", right:"0", marginRight:"15px", marginTop:"10px" }}></i>
        </Card.Header>
        <Accordion.Collapse eventKey={props.eventKey}>
          <Card.Body>
            <ContentHospitalSupervision {...props} onValidationChange={setHasErrors} />
          </Card.Body>
        </Accordion.Collapse>
      </Card>
    </>
  );
};

export const ContentHospitalSupervision = ({ onValidationChange, ...props }) => {
  return (
    <>
      <Alert
        variant="dark"
        className="text-center mt-4"
        style={{ fontSize: "14px" }}
      >
        Supervisión Hospitalaria
      </Alert>

      <Card>
        <Card.Body>
          <FormHospitalSupervision {...props} onValidationChange={onValidationChange} />
        </Card.Body>
      </Card>
    </>
  );
};

export const FormHospitalSupervision = ({ data, setCaseRecord, onValidationChange, ...props }) => {
  const lastHasErrorsRef = React.useRef(false);
  const shouldRegister = !props.disabledAll && props.enableMultiForm;
  const formikRef = useFormRegistration(shouldRegister ? 'hospitalData' : null);

  // Mapping from categorization to subcategorizations
  const overchargeOptions = [
    "Cirugía de Hombro",
    "Cirugía Rodilla",
    "Cirugía Columna",
    "Cirugía Ortopedia Otros",
    "Cualquier procedimiento quirúrgico no Ortopédico",
    "Medicamentos",
  ];

  const overutilizationOptions = [
    "Cardiología",
    "Cirugía de Hombro",
    "Cirugía Rodilla",
    "Cirugía Columna",
    "Cirugía Ortopedia Otros",
    "Cualquier procedimiento quirúrgico no Ortopedico",
    "Medicamentos",
  ];

  const qualityOptions = [
    "Cardiología",
    "Deducible",
    "Coaseguro Hospital",
    "Coaseguro Honorarios",
    "Penalización (Nivel, Gama, Zona)",
    "Cobranza de póliza",
    "Cobertura",
  ];

  const clarificationOptions = [
    "Sábana Quirúrgica",
    "Desglose de conceptos",
    "Notas de remisión",
    "Proveedores en convenio (GDL y MTY)",
  ];

  const lackOfInformationOptions = [
    "Informe médico",
    "Nota quirúrgica",
    "Estudios de Laboratorio y Gabinete",
    "Estado de cuenta",
  ];

  const subcategorizationsMap = {
    "Sobrecosto": overchargeOptions,
    "Sobreutilización": overutilizationOptions,
    "Sobreutilizacion": overutilizationOptions,
    "Calidad": qualityOptions,
    "Aclaración": clarificationOptions,
    "Aclaracion": clarificationOptions,
    "Falta de Información": lackOfInformationOptions,
    "Falta de Informacion": lackOfInformationOptions,
  };

  // All possible categories for the select
  const allCategories = [
    "Sobrecosto",
    "Sobreutilización",
    "Calidad",
    "Aclaración",
    "Falta de Información",
  ];

  // Product options
  const productOptions = [
    "LT",
    "LH",
    "FLEX",
    "PLUS",
    "AP",
  ];

  // Incident options
  const incidentOptions = [
    "Tratante",
    "Dictamen",
    "Control de Costos",
    "Hospital",
  ];

  // Incident type options
  const incidentTypeOptions = [
    "Coaseguro",
    "Cobertura",
    "Deducible",
    "Dictamen",
    "Neuromonitoreo",
    "Sobrecosto",
    "Sobreutilización",
    "Suma Asegurada",
  ];

  const [subcategorizations, setSubcategorizations] = useState([]);
  const [reviewCuts, setReviewCuts] = useState([]);
  const [showBinnacleModal, setShowBinnacleModal] = useState(false);

  const initialValues = {
    categorizationHospitalSupervision: data?.categorizationHospitalSupervision || "",
    incident: data?.incident || "",
    incidentType: data?.incidentType || "",
    subcategorizationHospitalSupervision: data?.subcategorizationHospitalSupervision || "",
    product: data?.product || "",
    managementHospitalAccount: data?.managementHospitalAccount || "",
    globalHospitalAccount: data?.globalHospitalAccount || "",
    containmentAmount: data?.containmentAmount || "",
  };

  const user = useAuthStore(state => state.user) || {};

  useEffect(() => {
    if (data.id) {
      getAllReviewCutsInit();
    }
  }, [data]);

  const getAllReviewCutsInit = () => {
    getAllReviewCuts(data.id).then((response) => {
      setReviewCuts(response.data);
    });
  }

  const handleCategorizationChange = (e, handleChange, setFieldValue) => {
    const selectedCategory = e.target.value;
    handleChange(e);

    // Update subcategorizations based on selected category
    setSubcategorizations(subcategorizationsMap[selectedCategory] || []);

    // Reset subcategorization field when category changes
    setFieldValue("subcategorizationHospitalSupervision", "");
  };

  const handleSubmitForm = (values, { setStatus, setSubmitting }) => {
    setTimeout(() => {
      update({ ...props.data, ...values }, data.id).then((response) =>
        console.log("Form hospital supervision data inside saved")
      );

      setSubmitting(false);
    }, 1000);
  };

  const handleSubmitReviewCuts = async (values, { setStatus, setSubmitting }) => {
    setTimeout(() => {
      update(values, data.id).then((response) => {
        setCaseRecord({ ...data, ...values });
        saveReviewCuts({ ...values, caseId: data.id, userId: user.idUser });
        getAllReviewCutsInit();
      });

      setSubmitting(false);
    }, 1000);
  }

  const { bindSubmitForm, bindErrors } = props;

  const validationSchema = Yup.object().shape({
    categorizationHospitalSupervision: Yup.string()
      .required("El campo es requerido"),
    incident: Yup.string()
      .required("El campo es requerido"),
    incidentType: Yup.string()
      .required("El campo es requerido"),
    subcategorizationHospitalSupervision: Yup.string()
      .required("El campo es requerido"),
    product: Yup.string()
      .required("El campo es requerido"),
    managementHospitalAccount: Yup.string()
      .required("El campo es requerido"),
    globalHospitalAccount: Yup.string()
      .required("El campo es requerido"),
  });

  const validationSchemaReviewCuts = Yup.object().shape({
    typeInvoice: Yup.string()
      .required("El campo es requerido"),
    invoiceATC: Yup.string()
      .required("El campo es requerido"),
  });

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={handleSubmitForm}
      >
        {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            status,
            isSubmitting,
            submitForm,
            submitCount,
            setFieldValue,
          }) => {
          if (bindSubmitForm) {
            bindSubmitForm(submitForm);
          }

          if (bindErrors) {
            bindErrors(errors);
          }

          if (onValidationChange) {
            const hasValidationErrors = submitCount > 0 && Object.keys(errors).length > 0;
            if (hasValidationErrors !== lastHasErrorsRef.current) {
              lastHasErrorsRef.current = hasValidationErrors;
              setTimeout(() => onValidationChange(hasValidationErrors), 0);
            }
          }
          return (
            <>
              <AlertError status={status} />
              <Form id="formu1" onSubmit={handleSubmit}>
                <Row>
                  <Col>
                    <Form.Group controlId="categorizationHospitalSupervision">
                      <Form.Label>CATEGORIZACIÓN SUPERVISIÓN HOSPITALARIA *</Form.Label>
                      <Form.Control
                        as="select"
                        name="categorizationHospitalSupervision"
                        value={values.categorizationHospitalSupervision}
                        onChange={(e) => handleCategorizationChange(e, handleChange, setFieldValue)}
                        disabled={props.disabledAll}
                        className={`${getInputClasses(
                          touched,
                          errors,
                          "categorizationHospitalSupervision"
                        )}`}
                      >
                        <option value="">SELECCIONA UNA OPCIÓN...</option>
                        {allCategories.map((category, idx) => (
                          <option key={idx} value={category}>
                            {category}
                          </option>
                        ))}
                      </Form.Control>
                      <ErrorMessage name="categorizationHospitalSupervision">
                        {(msg) => (
                          <Form.Control.Feedback type="invalid">
                            {msg}
                          </Form.Control.Feedback>
                        )}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>

                  <Col>
                    <Form.Group controlId="incident">
                      <Form.Label>INCIDENCIA *</Form.Label>
                      <Form.Control
                        as="select"
                        name="incident"
                        value={values.incident}
                        onChange={handleChange}
                        disabled={props.disabledAll}
                        className={`${getInputClasses(
                          touched,
                          errors,
                          "incident"
                        )}`}
                      >
                        <option value="">SELECCIONA UNA OPCIÓN...</option>
                        {incidentOptions.map((incident, idx) => (
                          <option key={idx} value={incident}>
                            {incident}
                          </option>
                        ))}
                      </Form.Control>
                      <ErrorMessage name="incident">
                        {(msg) => (
                          <Form.Control.Feedback type="invalid">
                            {msg}
                          </Form.Control.Feedback>
                        )}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>

                  <Col>
                    <Form.Group controlId="incidentType">
                      <Form.Label>TIPO DE INCIDENCIA *</Form.Label>
                      <Form.Control
                        as="select"
                        name="incidentType"
                        value={values.incidentType}
                        onChange={handleChange}
                        disabled={props.disabledAll}
                        className={`${getInputClasses(
                          touched,
                          errors,
                          "incidentType"
                        )}`}
                      >
                        <option value="">SELECCIONA UNA OPCIÓN...</option>
                        {incidentTypeOptions.map((type, idx) => (
                          <option key={idx} value={type}>
                            {type}
                          </option>
                        ))}
                      </Form.Control>
                      <ErrorMessage name="incidentType">
                        {(msg) => (
                          <Form.Control.Feedback type="invalid">
                            {msg}
                          </Form.Control.Feedback>
                        )}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group controlId="subcategorizationHospitalSupervision">
                      <Form.Label>SUBCATEGORIZACIÓN SUPERVISIÓN HOSPITALARIA *</Form.Label>
                      <Form.Control
                        as="select"
                        name="subcategorizationHospitalSupervision"
                        value={values.subcategorizationHospitalSupervision}
                        onChange={handleChange}
                        disabled={props.disabledAll}
                        className={`${getInputClasses(
                          touched,
                          errors,
                          "subcategorizationHospitalSupervision"
                        )}`}
                      >
                        <option value="">SELECCIONA UNA OPCIÓN...</option>
                        {subcategorizations.map((subcategory, idx) => (
                          <option key={idx} value={subcategory}>
                            {subcategory}
                          </option>
                        ))}
                      </Form.Control>
                      <ErrorMessage name="subcategorizationHospitalSupervision">
                        {(msg) => (
                          <Form.Control.Feedback type="invalid">
                            {msg}
                          </Form.Control.Feedback>
                        )}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>

                  <Col>
                    <Form.Group controlId="product">
                      <Form.Label>PRODUCTO *</Form.Label>
                      <Form.Control
                        as="select"
                        name="product"
                        value={values.product}
                        onChange={handleChange}
                        disabled={props.disabledAll}
                        className={`${getInputClasses(
                          touched,
                          errors,
                          "product"
                        )}`}
                      >
                        <option value="">SELECCIONA UNA OPCIÓN...</option>
                        {productOptions.map((product, idx) => (
                          <option key={idx} value={product}>
                            {product}
                          </option>
                        ))}
                      </Form.Control>
                      <ErrorMessage name="product">
                        {(msg) => (
                          <Form.Control.Feedback type="invalid">
                            {msg}
                          </Form.Control.Feedback>
                        )}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group controlId="globalHospitalAccount">
                      <Form.Label>CUENTA HOSPITALARIA GLOBAL *</Form.Label>
                      <InputGroup className="mb-3">
                        <InputGroup.Prepend>
                          <InputGroup.Text>$</InputGroup.Text>
                        </InputGroup.Prepend>
                        <NumberFormat
                          fixedDecimalScale={true}
                          decimalScale={2}
                          value={values.globalHospitalAccount}
                          onValueChange={({ floatValue }) => {
                            setFieldValue("globalHospitalAccount", floatValue ?? "");
                            const global = floatValue || 0;
                            const management = parseFloat(values.managementHospitalAccount) || 0;
                            setFieldValue("containmentAmount", (global - management).toFixed(2));
                          }}
                          name="globalHospitalAccount"
                          thousandSeparator={true}
                          disabled={props.disabledAll}
                          className={`form-control ${getInputClasses(
                            touched,
                            errors,
                            "globalHospitalAccount"
                          )}`}
                        />
                      </InputGroup>
                      <ErrorMessage name="globalHospitalAccount">
                        {(msg) => (
                          <Form.Control.Feedback type="invalid">
                            {msg}
                          </Form.Control.Feedback>
                        )}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group controlId="managementHospitalAccount">
                      <Form.Label>CUENTA HOSPITALARIA CON GESTIÓN *</Form.Label>
                      <InputGroup className="mb-3">
                        <InputGroup.Prepend>
                          <InputGroup.Text>$</InputGroup.Text>
                        </InputGroup.Prepend>
                        <NumberFormat
                          fixedDecimalScale={true}
                          decimalScale={2}
                          value={values.managementHospitalAccount}
                          onValueChange={({ floatValue }) => {
                            setFieldValue("managementHospitalAccount", floatValue ?? "");
                            const global = parseFloat(values.globalHospitalAccount) || 0;
                            const management = floatValue || 0;
                            setFieldValue("containmentAmount", (global - management).toFixed(2));
                          }}
                          name="managementHospitalAccount"
                          thousandSeparator={true}
                          disabled={props.disabledAll}
                          className={`form-control ${getInputClasses(
                            touched,
                            errors,
                            "managementHospitalAccount"
                          )}`}
                        />
                      </InputGroup>
                      <ErrorMessage name="managementHospitalAccount">
                        {(msg) => (
                          <Form.Control.Feedback type="invalid">
                            {msg}
                          </Form.Control.Feedback>
                        )}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Form.Group controlId="containmentAmount">
                      <Form.Label>MONTO DE CONTENCIÓN (AUTOMÁTICO)</Form.Label>
                      <InputGroup className="mb-3">
                        <InputGroup.Prepend>
                          <InputGroup.Text>$</InputGroup.Text>
                        </InputGroup.Prepend>
                        <NumberFormat
                          fixedDecimalScale={true}
                          decimalScale={2}
                          value={values.containmentAmount}
                          name="containmentAmount"
                          thousandSeparator={true}
                          disabled={true}
                          className={`form-control`}
                        />
                      </InputGroup>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting && (
                        <Spinner
                          as="span"
                          animation="border"
                          variant="dark"
                          size="sm"
                        />
                      )}
                      Guardar
                    </Button>
                  </Col>
                </Row>
              </Form>
            </>
          );
        }}
      </Formik>

      <hr />

      <Formik
        initialValues={{ ...initialValues }}
        enableReinitialize={true}
        onSubmit={handleSubmitReviewCuts}
        validationSchema={validationSchemaReviewCuts}
      >
        {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            status,
            isSubmitting,
            submitForm,
            setFieldValue,
          }) => {
          return (
            <>
              <AlertError status={status} />
              <Form onSubmit={handleSubmit}>
                <Row>
                  <Col>
                    <Form.Group controlId="invoiceATC">
                      <Form.Label>Folio ATC:</Form.Label>
                      <Form.Control
                        name="invoiceATC"
                        value={values.invoiceATC || ""}
                        onChange={handleChange}
                        className={`${getInputClasses(
                          touched,
                          errors,
                          "invoiceATC"
                        )}`}
                      />
                      <ErrorMessage name="invoiceATC">
                        {(msg) => (
                          <Form.Control.Feedback type="invalid">
                            {msg}
                          </Form.Control.Feedback>
                        )}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>

                  <Col>
                    <Form.Group controlId="typeInvoice">
                      <Form.Label>Tipo de folio ATC:</Form.Label>
                      <Form.Control
                        as="select"
                        name="typeInvoice"
                        value={values.typeInvoice || ""}
                        onChange={handleChange}
                        className={`${getInputClasses(
                          touched,
                          errors,
                          "typeInvoice"
                        )}`}
                      >
                        <option value="">SELECCIONE UNA OPCIÓN....</option>
                        <option value="Alta"> Alta </option>
                        <option value="Corte">Corte</option>
                      </Form.Control>
                      <ErrorMessage name="atcTypeTicket">
                        {(msg) => (
                          <Form.Control.Feedback type="invalid">
                            {msg}
                          </Form.Control.Feedback>
                        )}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isSubmitting}
                    >
                      {isSubmitting && (
                        <Spinner
                          as="span"
                          animation="border"
                          variant="dark"
                          size="sm"
                        />
                      )}
                      Guardar
                    </Button>
                  </Col>
                </Row>
              </Form>
            </>
          );
        }}
      </Formik>

      <ListReviewCuts
        reviewCuts={reviewCuts}
        hideColumns={{
          caseType: true,
          createdAt: true,
          user: true,
        }} />

      <Row className="mt-3">
        <Col>
          <Button
            variant="info"
            onClick={() => setShowBinnacleModal(true)}
          >
            <i className="fa fa-comments mr-2"></i>
            Bitácora de Supervisión Hospitalaria
          </Button>
        </Col>
      </Row>

      <HospitalSupervisionBinnacleModal
        show={showBinnacleModal}
        handleClose={() => setShowBinnacleModal(false)}
        caseId={data?.id}
        caseData={data}
        slug={data?.slug}
      />
    </>
  );
};

